import type { FormDataType } from 'contexts/FormProvider';

// ─── Address type identifier ─────────────────────────────────────────────────

export type AddressType = 'physical' | 'postal' | 'repPostal';

// ─── Field map shape ─────────────────────────────────────────────────────────

interface AddressFieldMap {
  /** Value used as the AddressSearch `fieldName` prop */
  fieldName: string;
  /** Top-level address string (lookup display value) */
  address: keyof FormDataType;
  /** Raw AddressList item stored for re-hydration */
  addressObject: keyof FormDataType;
  streetAddress: keyof FormDataType;
  suburb: keyof FormDataType;
  state: keyof FormDataType;
  postcode: keyof FormDataType;
  country: keyof FormDataType;
  isAddressValid: keyof FormDataType;
  isManualAddress: keyof FormDataType;
  /** Only physical address tracks a separate manual-validity flag */
  isManualAddressValid?: keyof FormDataType;
}

// ─── Field maps per address type ─────────────────────────────────────────────

export const ADDRESS_FIELD_MAPS: Record<AddressType, AddressFieldMap> = {
  physical: {
    fieldName: 'physicalAddress',
    address: 'physicalAddress',
    addressObject: 'physicalAddressObject',
    streetAddress: 'phyStreetAddress',
    suburb: 'phySuburb',
    state: 'phyState',
    postcode: 'phyPostcode',
    country: 'phyCountry',
    isAddressValid: 'isPhysAddressValid',
    isManualAddress: 'isManualPhysAddress',
    isManualAddressValid: 'isManualPhysAddressValid',
  },
  postal: {
    fieldName: 'postalAddress',
    address: 'postalAddress',
    addressObject: 'postalAddressObject',
    streetAddress: 'posStreetAddress',
    suburb: 'posSuburb',
    state: 'posState',
    postcode: 'posPostcode',
    country: 'posCountry',
    isAddressValid: 'isPosAddressValid',
    isManualAddress: 'isManualPosAddress',
  },
  repPostal: {
    fieldName: 'repPostalAddress',
    address: 'repPostalAddress',
    addressObject: 'repPostalAddressObject',
    streetAddress: 'repPosStreetAddress',
    suburb: 'repPosSuburb',
    state: 'repPosState',
    postcode: 'repPosPostcode',
    country: 'repPosCountry',
    isAddressValid: 'isRepPosAddressValid',
    isManualAddress: 'isManualRepPosAddress',
  },
};

// ─── Address labels ─────────────────────────────────────────────────────────────

const ADDRESS_LABELS: Record<AddressType, string> = {
  postal: 'Postal Address',
  repPostal: 'Postal Address',
  physical: 'Residential Address',
};

export function getAddressLabel(type: AddressType): string {
  return ADDRESS_LABELS[type];
}
